import {
  formatJstDateTime,
  instantFromIso,
  nowIsoUtc,
} from "@nijiurachan/js/util/time";
import { useEffect, useRef, useState } from "react";
import { BOARD_DISPLAY_NAME } from "@/config/browserBoard";
import { waitForBrowserAuthorization } from "@/features/app-auth/api/appAuth";
import { useAppAuthMetadata } from "@/features/app-auth/useAppAuthMetadata";
import { apiDelete, apiGet } from "@/shared/api/client";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { Button } from "@/shared/ui/form";

interface ConnectedApplication {
  id: string;
  clientName: string;
  issuedAt: string;
  expiresAt: string;
  revokedAt: string | null;
}

export const ConnectedApplicationsPage: React.FunctionComponent = () => {
  useDocumentTitle(`接続済みアプリ - ${BOARD_DISPLAY_NAME}`);
  useAppAuthMetadata();
  const [applications, setApplications] = useState<
    ConnectedApplication[] | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const active = useRef<AbortController | null>(null);
  const token = useRef<string | undefined>(undefined);
  useEffect(
    () => (): void => {
      active.current?.abort();
      token.current = undefined;
    },
    [],
  );

  const load = (): void => {
    active.current?.abort();
    const request = new AbortController();
    active.current = request;
    setBusy(true);
    setMessage("ブラウザ認証と接続済みアプリを確認しています…");
    void (async (): Promise<void> => {
      try {
        const browserToken = await waitForBrowserAuthorization(request.signal);
        const rows = await apiGet<ConnectedApplication[]>("/app-credentials", {
          token: browserToken,
          credentials: "include",
          cache: "no-store",
          signal: request.signal,
        });
        if (request.signal.aborted) return;
        token.current = browserToken;
        setApplications(rows);
        setMessage(
          rows.length === 0
            ? "接続済みアプリはありません。"
            : "取り消すアプリを選択してください。",
        );
      } catch (error) {
        if (!request.signal.aborted)
          setMessage(
            error instanceof Error
              ? error.message
              : "一覧を取得できませんでした。",
          );
      } finally {
        if (!request.signal.aborted) setBusy(false);
      }
    })();
  };
  const revoke = (id: string): void => {
    if (!token.current || busy) return;
    const request = new AbortController();
    active.current = request;
    setBusy(true);
    void apiDelete(
      `/app-credentials/${id}`,
      {},
      { token: token.current, credentials: "include", signal: request.signal },
    )
      .then(() => {
        if (request.signal.aborted) return;
        setApplications(
          (rows) =>
            rows?.map((row) =>
              row.id === id ? { ...row, revokedAt: nowIsoUtc() } : row,
            ) ?? null,
        );
        setMessage("アプリの利用許可を取り消しました。");
      })
      .catch((error: unknown) => {
        if (!request.signal.aborted)
          setMessage(
            error instanceof Error ? error.message : "取り消しに失敗しました。",
          );
      })
      .finally(() => {
        if (!request.signal.aborted) setBusy(false);
      });
  };
  return (
    <section className="flex-1 overflow-y-auto" aria-label="接続済みアプリ">
      <div className="mx-auto max-w-xl space-y-5 px-4 py-8">
        <h1 className="text-xl font-bold">接続済みアプリ</h1>
        <p>
          このブラウザで許可したアプリを確認し、端末ごとに利用許可を取り消せます。
        </p>
        <Button disabled={busy} onClick={load}>
          接続済みアプリを確認
        </Button>
        <p role="status" aria-live="polite">
          {message}
        </p>
        <ul className="space-y-4">
          {applications?.map((application) => (
            <li
              key={application.id}
              className="rounded-lg border border-border p-4"
            >
              <strong>{application.clientName}</strong>
              <p>許可日時: {formatTimestamp(application.issuedAt)}</p>
              <p>有効期限: {formatTimestamp(application.expiresAt)}</p>
              {application.revokedAt !== null ? (
                <p>取り消し済み</p>
              ) : (
                <Button
                  variant="destructive"
                  disabled={busy}
                  onClick={() => revoke(application.id)}
                >
                  この端末の許可を取り消す
                </Button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

function formatTimestamp(value: string): string {
  const instant = instantFromIso(value);
  return instant === null
    ? "不明"
    : formatJstDateTime(instant, { dateStyle: "medium", timeStyle: "short" });
}
